// @flow
import React from 'react';
import type { ChildrenArray } from 'react';
import {
  types as t,
  usePropsValidation,
} from '../../validation/runtime-checks/index.js';
import { loadImage } from '../../loadImage.js';
import type { CompilerInjectedProps } from '../../validation/runtime-checks/index.js';
import { Text } from '../../Text.js';
import Image from '../../Image.js';
import { CartesianPlane } from '../../CartesianPlane.js';
import { productColor } from '../../designSystem.js';
import { ContentContext } from '../../JsxContent.js';
import { illustrationColor, fontFamilies } from '../../designSystem.js';
import { InternalGraphic, getGraphicSelections } from './InternalGraphic.js';
import Figure from '../../Figure.js';
import { INVInternalLessonLink } from './INVInternalLessonLink.js';
import { INVExplainedEquation } from './INVExplainedEquation.js';
import { INVGeogebra } from './INVGeogebra.js';
import { INVWistiaVideo } from './INVWistiaVideo.js';
import { INVBunnyVideo } from './INVBunnyVideo.js';
import type { MathString } from '../../types.js';
import { pathChecker } from '../../Image.js';
import { mathStringChecker } from '../../types.js';
import { NumberLine } from '../../NumberLine.js';
import { StemAndLeafPlot } from '../../StemAndLeafPlot.js';
import { AlgebraicWorking } from '../../AlgebraicWorking.js';
import { PseudoCodeBlock } from '../../PseudoCodeBlock.js';
import TabularData from '../../TabularData.js';
import BoxAndWhiskerPlot from '../../BoxAndWhiskerPlot.js';
import DraftImage from '../../DraftImage.js';
import LatexInternal from '../../LatexInternal.js';
import { childrenArrayMatcher } from '../../childrenArrayMatcher.js';

export function INVInformation(props: {|
  children: ChildrenArray<
    | React$Element<typeof INVInformationChunks>
    | React$Element<typeof INVExpand>,
  >,
  ...CompilerInjectedProps,
|}) {
  const { children } = props;

  const { failedValidation } = usePropsValidation(props, INVInformation);
  if (failedValidation) return null;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        rowGap: 24,
      }}
    >
      {children}
    </div>
  );
}
INVInformation.spec = {
  children: t.childrenArray(
    t.unionOfTypes(
      t.elementType(INVInformationChunks),
      t.elementType(INVExpand),
    ),
  ),
};

export function INVInformationChunks(props: {|
  shuffleGraphicsSeed: string,
  children: ChildrenArray<React$Element<typeof INVChunk>>,
  ...CompilerInjectedProps,
|}) {
  const { enablePhoneLayout } = React.useContext(ContentContext);
  const { failedValidation } = usePropsValidation(props, INVInformationChunks);
  if (failedValidation) return null;

  const { shuffleGraphicsSeed, children } = props;
  const graphicSelections = getGraphicSelections(shuffleGraphicsSeed);
  const solidSelections = getGraphicSelections(shuffleGraphicsSeed, 'solids');

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: enablePhoneLayout ? 24 : 0,
        alignItems: 'flex-start',
      }}
    >
      {React.Children.map(children, (child, idx) =>
        // $FlowIssue
        React.cloneElement(child, {
          _index: idx,
          _graphicSelection: graphicSelections[idx % graphicSelections.length],
          _solidSelection: solidSelections[idx % solidSelections.length],
        }),
      )}
    </div>
  );
}
INVInformationChunks.spec = {
  shuffleGraphicsSeed: t.uuid,
  children: t.childrenArray(t.elementType(INVChunk)),
};

// This is basically a free-form space to put legacy
// components that don't fit into the mobile friendly layout
// Collapsible?
export function INVExpand(props: {|
  children: ChildrenArray<
    | React$Element<typeof Text>
    | React$Element<typeof Image>
    | React$Element<typeof DraftImage>
    | React$Element<typeof TabularData>
    | React$Element<typeof BoxAndWhiskerPlot>
    | React$Element<typeof CartesianPlane>
    | React$Element<typeof NumberLine>
    | React$Element<typeof StemAndLeafPlot>
    | React$Element<typeof AlgebraicWorking>
    | React$Element<typeof PseudoCodeBlock>
    | React$Element<typeof INVInternalLessonLink>
    | React$Element<typeof INVExplainedEquation>
    | React$Element<typeof INVGeogebra>
    | React$Element<typeof INVWistiaVideo>
    | React$Element<typeof INVBunnyVideo>,
  >,
  ...CompilerInjectedProps,
|}) {
  const { children } = props;

  const { failedValidation } = usePropsValidation(props, INVExpand);
  if (failedValidation) return null;

  return (
    <div
      style={{
        backgroundColor: productColor.aliceBlue,
        border: `2px solid ${productColor.tropicalBlue}`,
        borderRadius: 8,
        padding: 20,
      }}
    >
      <div>{children}</div>
    </div>
  );
}
INVExpand.spec = {
  children: t.childrenArray(
    t.unionOfTypes(
      t.elementType(Text),
      t.elementType(Image),
      t.elementType(DraftImage),
      t.elementType(TabularData),
      t.elementType(BoxAndWhiskerPlot),
      t.elementType(CartesianPlane),
      t.elementType(NumberLine),
      t.elementType(StemAndLeafPlot),
      t.elementType(AlgebraicWorking),
      t.elementType(PseudoCodeBlock),
      t.elementType(INVInternalLessonLink),
      t.elementType(INVExplainedEquation),
      t.elementType(INVGeogebra),
      t.elementType(INVWistiaVideo),
      t.elementType(INVBunnyVideo),
    ),
  ),
};

export function INVChunk(props: {|
  children: ChildrenArray<
    // graphic-like components
    | React$Element<typeof INVChunkFocus>
    | React$Element<typeof INVChunkImage>
    | React$Element<typeof DraftImage>
    | React$Element<typeof INVChunkLatex>
    //text-like components
    | React$Element<typeof INVChunkText>
    | React$Element<typeof INVChunkList>,
  >,
  _index: number,
  _graphicSelection: number,
  _solidSelection: number,
  ...CompilerInjectedProps,
|}) {
  const { enablePhoneLayout } = React.useContext(ContentContext);
  const [visual, text] = React.Children.toArray(props.children);

  const { _index, _graphicSelection, _solidSelection } = props;
  const isReversed = _index % 2 === 1;

  const { failedValidation } = usePropsValidation(props, INVChunk);
  if (failedValidation) return null;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: enablePhoneLayout ? 'flex-start' : 'center',
        width: '100%',
        flexDirection: enablePhoneLayout
          ? 'column'
          : isReversed
            ? 'row-reverse'
            : 'row',
      }}
    >
      <div
        style={{
          width: 284,
          height: 284,
          boxShadow: '0px 8.75px 49px rgba(62, 62, 76, 0.15)',
          borderRadius: 12,
          zIndex: 1,
          flexShrink: 0,
        }}
      >
        {/* $FlowIssue bad cloneElement signature */}
        {React.cloneElement(visual, {
          ...([INVChunkFocus.name, INVChunkLatex.name].includes(
            visual.type.name,
          )
            ? {
                _graphicSelection:
                  visual.type.name === INVChunkLatex.name
                    ? _solidSelection
                    : _graphicSelection,
              }
            : {}),
        })}
      </div>
      <div
        style={{
          backgroundColor: illustrationColor.porcelain,
          width: '100%',
          borderRadius: 12,
          flexGrow: 1,
          ...(enablePhoneLayout
            ? { padding: '28px 16px 16px 16px', margin: '-12px 0 16px' }
            : isReversed
              ? {
                  padding: '16px 28px 16px 16px',
                  margin: '16px -12px 16px   0px',
                }
              : {
                  padding: '16px 16px 16px 28px',
                  margin: '16px   0px 16px -12px',
                }),
        }}
      >
        {text}
      </div>
    </div>
  );
}
INVChunk.spec = {
  children: t.unionOfTypes(
    t.exactOrderChildrenArray(
      t.elementType(INVChunkFocus),
      t.elementType(INVChunkText),
    ),
    t.exactOrderChildrenArray(
      t.elementType(INVChunkImage),
      t.elementType(INVChunkText),
    ),
    t.exactOrderChildrenArray(
      t.elementType(DraftImage),
      t.elementType(INVChunkText),
    ),
    t.exactOrderChildrenArray(
      t.elementType(INVChunkLatex),
      t.elementType(INVChunkText),
    ),
    t.exactOrderChildrenArray(
      t.elementType(INVChunkFocus),
      t.elementType(INVChunkList),
    ),
    t.exactOrderChildrenArray(
      t.elementType(INVChunkImage),
      t.elementType(INVChunkList),
    ),
    t.exactOrderChildrenArray(
      t.elementType(INVChunkLatex),
      t.elementType(INVChunkList),
    ),
    t.exactOrderChildrenArray(
      t.elementType(DraftImage),
      t.elementType(INVChunkList),
    ),
  ),
};

const commonChunkStyle = {
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 12,
};

export function INVChunkFocus(props: {|
  text?: string,
  highlight?: boolean,
  _graphicSelection: number,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(props, INVChunkFocus);

  if (failedValidation) return null;

  const { text, highlight, _graphicSelection } = props;
  const noText = text === undefined || text === '';

  return (
    <div
      style={{
        ...commonChunkStyle,
        backgroundColor: highlight
          ? illustrationColor.iceberg
          : productColor.papayaWhip,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        <InternalGraphic
          selection={_graphicSelection}
          palette={highlight ? 'green' : 'orange'}
        />
        <div
          style={{
            fontWeight: fontFamilies.gilroy.weights.extraBold,
            fontSize: 40,
            color: highlight
              ? illustrationColor.genoa
              : illustrationColor.cinnamon,
            fontFamily: fontFamilies.gilroy.name,

            position: 'absolute',
            textAlign: 'center',
            padding: 16,
          }}
        >
          {noText ? (
            <svg width="96" height="96" viewBox="0 0 96 96" fill="none">
              <rect
                width="96"
                height="96"
                rx="48"
                fill={
                  highlight
                    ? illustrationColor.genoa
                    : illustrationColor.cinnamon
                }
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M49.8285 24.6857C49.8285 25.6956 49.0098 26.5143 47.9999 26.5143C46.99 26.5143 46.1713 25.6956 46.1713 24.6857V21.0286C46.1713 20.0187 46.99 19.2 47.9999 19.2C49.0098 19.2 49.8285 20.0187 49.8285 21.0286V24.6857ZM27.138 30.3968C26.3644 29.7477 26.2635 28.5943 26.9126 27.8207C27.5618 27.0471 28.7152 26.9461 29.4888 27.5953L32.2903 29.9461C33.064 30.5952 33.1649 31.7486 32.5157 32.5222C31.8666 33.2958 30.7132 33.3967 29.9396 32.7476L27.138 30.3968ZM22.3999 44.8C22.3999 45.8099 23.2186 46.6286 24.2285 46.6286H27.8856C28.8955 46.6286 29.7142 45.8099 29.7142 44.8C29.7142 43.7901 28.8955 42.9714 27.8856 42.9714H24.2285C23.2186 42.9714 22.3999 43.7901 22.3999 44.8ZM66.2856 44.8C66.2856 45.8099 67.1043 46.6286 68.1142 46.6286H71.7713C72.7812 46.6286 73.5999 45.8099 73.5999 44.8C73.5999 43.7901 72.7812 42.9714 71.7713 42.9714H68.1142C67.1043 42.9714 66.2856 43.7901 66.2856 44.8ZM66.0602 32.7476L68.8618 30.3968C69.6354 29.7477 69.7363 28.5943 69.0872 27.8207C68.438 27.0471 67.2846 26.9461 66.511 27.5953L63.7095 29.9461C62.9359 30.5952 62.8349 31.7486 63.4841 32.5222C64.1332 33.2958 65.2866 33.3967 66.0602 32.7476ZM47.979 30.1717C44.384 30.1615 40.9133 31.4504 38.2407 33.7882C35.1447 36.4925 33.3748 40.3474 33.3713 44.3938C33.3693 47.6879 34.5521 50.8791 36.715 53.4147C36.7206 53.4146 36.726 53.4167 36.7299 53.4205C36.7338 53.4244 36.736 53.4296 36.7359 53.435C36.9696 53.7269 37.2208 54.005 37.4882 54.268C40.2502 57.0772 41.7728 60.8252 41.7305 64.7111C41.7339 65.8319 42.6675 66.7396 43.8203 66.7429H52.1795C53.3323 66.7396 54.2659 65.8319 54.2693 64.7111C54.2749 63.7302 54.3658 62.7515 54.541 61.7854C54.9183 59.8981 55.663 58.0982 56.7351 56.4826C57.2629 55.6624 57.8795 54.8996 58.5744 54.2071V54.1868C58.804 53.9633 59.0131 53.7195 59.2221 53.4757C59.2325 53.4655 59.2378 53.4553 59.243 53.4452C59.2482 53.435 59.2534 53.4249 59.2639 53.4147C61.4356 50.8747 62.6258 47.6765 62.6285 44.3735C62.6273 43.6114 62.5643 42.8505 62.4401 42.098C61.3594 35.8323 56.16 31.0089 49.6717 30.253C49.1095 30.1955 48.5444 30.1684 47.979 30.1717ZM46.5427 36.2654C47.4033 36.1143 47.9784 35.2942 47.8273 34.4336C47.6762 33.573 46.8561 32.9978 45.9955 33.1489C39.5475 34.2811 36.1142 38.0664 36.1142 44.1589C36.1142 45.0327 36.8225 45.741 37.6963 45.741C38.5701 45.741 39.2784 45.0327 39.2784 44.1589C39.2784 39.6561 41.5603 37.1403 46.5427 36.2654ZM42.5141 72.8972C42.5141 74.917 44.6312 76.5543 45.2371 76.5543H50.6889C51.2949 76.5543 53.4855 74.917 53.4855 72.8972V69.24H42.5141V72.8972Z"
                fill={productColor.white}
              />
            </svg>
          ) : (
            text
          )}
        </div>
      </div>
    </div>
  );
}
INVChunkFocus.spec = {
  text: t.optional(t.string),
  highlight: t.optional(t.boolean),
};

export function INVChunkImage(props: {|
  path: string,
  accessibilityDescription: string,
  ...CompilerInjectedProps,
|}) {
  const ORIGIN = 'https://jsx-images.mathspace.co';
  const { path, accessibilityDescription, _performValidation } = props;

  const { failedValidation, addErrors, locations } = usePropsValidation(
    props,
    INVChunkImage,
  );

  React.useEffect(() => {
    if (failedValidation || !_performValidation) return;
    assertFileIsSvg(props.path, addErrors, locations);
  });

  function validateDimensions() {
    let staleEffect = false;
    if (failedValidation || !_performValidation) return;
    (async () => {
      try {
        const imageNode = await loadImage(`${ORIGIN}${path}`);
        if (staleEffect) return;
        const { naturalWidth, naturalHeight } = imageNode;
        if (naturalWidth !== 260 || naturalHeight !== 260) {
          addErrors([
            {
              message: `Only images that are naturally 260x260px are allowed in INVChunkImage`,
              ...locations.INVChunkImage,
            },
          ]);
        }
      } catch {
        if (staleEffect) return;
        addErrors([
          {
            message: `The image at ${path} could not be loaded.`,
            ...locations.path,
          },
        ]);
      }
    })();
    return () => {
      staleEffect = true;
    };
  }

  React.useEffect(validateDimensions);

  if (failedValidation) return null;

  return (
    <div
      style={{
        ...commonChunkStyle,
        backgroundColor: productColor.white,
      }}
    >
      <Figure _removeFigureMargin>
        <img
          src={`${ORIGIN}${path}`}
          alt={accessibilityDescription}
          width={260}
          height={260}
          onLoad={validateDimensions}
        />
      </Figure>
    </div>
  );

  function assertFileIsSvg(path, addErrors, locations) {
    const { pathname } = new URL('http://fake.com' + props.path);
    const extensionMatch = pathname.match(/\.(svg|SVG)$/);
    if (extensionMatch === null) {
      addErrors([
        {
          message: `You can only specify a path to an svg image.`,
          ...locations.path,
        },
      ]);
      return;
    } else assertValidSvgContents(`${ORIGIN}${path}`, addErrors, locations);
  }

  // TODO async assertions are going to wreak havoc with the context-based system
  // if we aren't careful. As the the runtime validation system relies upon all the
  // validation completing within a given commit cycle.
  async function assertValidSvgContents(url, addErrors, locations) {
    const res = await fetch(url);
    const svg = await res.text();
    const div = document.createElement('div');
    div.innerHTML = svg;
    const svgElement = div.querySelector('svg');
    if (svgElement === null) return;

    if (svgElement.querySelector('image') !== null) {
      addErrors([
        {
          message:
            'The svg image you are trying to use contains an embedded bitmap. This defeats the purpose of using vector graphics, so is not allowed.',
          ...locations.path,
        },
      ]);
    }
    // If there are any <text> nodes, then there exists text that has not had
    // the convert to outlines applied.
    if (svgElement.querySelector('text') !== null) {
      addErrors([
        {
          message:
            'The svg image you are trying to use contains fonts that have not been converted to outlines. This violates our image style guide.',
          ...locations.path,
        },
      ]);
    }
  }
}
INVChunkImage.spec = {
  path: pathChecker,
  accessibilityDescription: t.string,
};

export function INVChunkLatex(props: {|
  expression: string,
  _graphicSelection: number,
  ...CompilerInjectedProps,
|}) {
  const { expression, _graphicSelection, _performValidation } = props;

  const { failedValidation, addErrors, locations } = usePropsValidation(
    props,
    INVChunkLatex,
  );

  const katexRenderSizeRef = React.useRef(null);

  React.useEffect(() => {
    if (failedValidation || !_performValidation) return;
    if (katexRenderSizeRef.current === null) return;
    const { width, height } =
      katexRenderSizeRef.current.getBoundingClientRect();
    if (width > 260 || height > 260) {
      addErrors([
        {
          message: `This LaTeX expression is too large to be displayed in an INVChunkLatex.`,
          ...locations.expression,
        },
      ]);
    }
  });

  if (failedValidation) return null;
  return (
    <div
      style={{
        ...commonChunkStyle,
        backgroundColor: productColor.tropicalBlue,
        color: illustrationColor.regalBlue,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        <InternalGraphic
          selection={_graphicSelection}
          partition="solids"
          palette="blue"
        />
        <div
          ref={katexRenderSizeRef}
          style={{
            position: 'absolute',
          }}
        >
          <LatexInternal displayMode expression={expression} fontSize={24} />
        </div>
      </div>
    </div>
  );
}
INVChunkLatex.spec = {
  expression: t.latex,
};

export function INVChunkText(props: {|
  title: string,
  children: ChildrenArray<
    MathString | React$Element<typeof INVInternalLessonLink>,
  >,
  ...CompilerInjectedProps,
|}) {
  const { children, title } = props;

  const { failedValidation } = usePropsValidation(props, INVChunkText);
  if (failedValidation) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ fontWeight: 600 }}>{title}</div>
      <div>{children}</div>
    </div>
  );
}
INVChunkText.spec = {
  title: t.string,
  children: t.childrenArray(
    t.unionOfTypes(mathStringChecker, t.elementType(INVInternalLessonLink)),
  ),
};

export function INVChunkList(props: {|
  title: string,
  children: ChildrenArray<React$Element<typeof INVChunkListItem>>,
  ...CompilerInjectedProps,
|}) {
  const { children, title } = props;

  const { failedValidation } = usePropsValidation(props, INVChunkList);
  if (failedValidation) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ fontWeight: 600 }}>{title}</div>
      <ul style={{ margin: 0 }}>
        {React.Children.map(children, (child, idx) => (
          <li key={idx}>{child}</li>
        ))}
      </ul>
    </div>
  );
}
INVChunkList.spec = {
  title: t.string,
  children: childrenArrayMatcher({
    // we want this used as a list, so there needs to be multiple items
    min: 2,
    max: Infinity,
    elementType: t.elementType(INVChunkListItem),
  }),
};

export function INVChunkListItem(props: {|
  children: ChildrenArray<
    MathString | React$Element<typeof INVInternalLessonLink>,
  >,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(props, INVChunkListItem);
  if (failedValidation) return null;

  return props.children;
}
INVChunkListItem.spec = {
  children: t.childrenArray(
    t.unionOfTypes(mathStringChecker, t.elementType(INVInternalLessonLink)),
  ),
};
